import React from 'react'
import { Icons } from '@pankod/refine-antd'
import { useCan, useCreate } from '@pankod/refine-core'

import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import SelfEnrollmentConfigDrawer, {
  useSelfEnrollmentConfigDrawer,
} from '../../../../program-setup/blocks/self-enrollment/SelfEnrollmentConfigDrawer'
import MenuItemPopconfirm from '../../MenuItemPopconfirm'
import { Highlighter } from '../../utils'
import { StatusTag } from '../StatusTag'

import type {
  TConfigurationSection,
  TConfigurationSectionContext,
} from '../types'

export const useSelfEnrollmentConfiguration = ({
  invalidateProgramDetail,
  isOwner,
  program,
  programId,
  runAndClose,
  search,
}: TConfigurationSectionContext): TConfigurationSection => {
  const { mutateAsync: toggleSelfEnrollment } = useCreate()
  const { show: showConfig, ...drawerProps } = useSelfEnrollmentConfigDrawer()
  const { data: canAccess } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'self-enrollment' },
  })
  const { data: canToggle } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'toggle-self-enrollment' },
  })
  const { data: canManageConfig } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'self-enrollment-config' },
  })
  const active = program?.isSelfEnrollment
  const canToggleStatus = canToggle?.can || isOwner

  return {
    menuItems:
      canAccess?.can || isOwner
        ? [
            {
              key: 'Pendaftaran Mandiri',
              type: 'group',
              label: (
                <div className="flex justify-between gap-2">
                  <Highlighter search={search} text="Pendaftaran Mandiri" />
                  <StatusTag
                    active={!!active}
                    activeText="AKTIF"
                    inactiveText="NONAKTIF"
                  />
                </div>
              ),
              children: [
                {
                  icon: active ? (
                    <Icons.StopOutlined />
                  ) : (
                    <Icons.CheckSquareOutlined />
                  ),
                  key: active
                    ? 'Nonaktifkan Pendaftaran Mandiri'
                    : 'Aktifkan Pendaftaran Mandiri',
                  danger: active,
                  disabled: !canToggleStatus,
                  label: (
                    <MenuItemPopconfirm
                      title={`Apakah Anda ingin ${
                        active ? 'me-nonaktifkan' : 'mengaktifkan'
                      } pendaftaran mandiri?`}
                      okText={active ? 'Nonaktifkan' : 'Aktifkan'}
                      cancelText="Batal"
                      disabled={!canToggleStatus}
                      okButtonProps={{ danger: !!active }}
                      onConfirm={() =>
                        toggleSelfEnrollment(
                          {
                            resource: `programs/${programId}/self-enrollment/status`,
                            dataProviderName: 'lms',
                            metaData: { httpMethod: 'put' },
                            values: { active: !active },
                            successNotification: {
                              message:
                                'Konfigurasi pendaftaran mandiri berhasil diperbarui',
                              type: 'success',
                              description: 'Sukses',
                            },
                            errorNotification: (error) =>
                              showErrorNotification(
                                error,
                                'Terdapat gangguan saat mengubah status pendaftaran mandiri',
                              ),
                          },
                          { onSuccess: invalidateProgramDetail },
                        )
                      }
                    >
                      <Highlighter
                        search={search}
                        text={
                          active
                            ? 'Nonaktifkan Pendaftaran Mandiri'
                            : 'Aktifkan Pendaftaran Mandiri'
                        }
                      />
                    </MenuItemPopconfirm>
                  ),
                },
                {
                  icon: <Icons.SettingOutlined />,
                  key: 'Kelola Pendaftaran Mandiri',
                  disabled: !(canManageConfig?.can || isOwner),
                  label: (
                    <Highlighter
                      search={search}
                      text="Kelola Pendaftaran Mandiri"
                    />
                  ),
                  onClick: () => runAndClose(() => showConfig(programId)),
                },
              ],
            },
          ]
        : [],
    overlays: (
      <SelfEnrollmentConfigDrawer
        {...drawerProps}
        onSubmitted={invalidateProgramDetail}
      />
    ),
  }
}
